import React, { useState, useEffect, useCallback } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import {
  fetchTestBankItem,
  createTestBankItem,
  updateTestBankItem,
  deleteTestBankItem,
  fetchTestBankAssignments,
  fetchTestBankVersions,
  unassignTestFromCourse,
} from "../../api/backend";
import useAssignableClasses from "../../hooks/useAssignableClasses";
import AssignClassRow from "../../components/assignClass/AssignClassRow";
import CreateTestModal from "../../components/CreateTestModal";
import { assignBankTestAsClassLevel } from "../courseHub/bankTestClone";

const D = {
  ink: "var(--color-ink)", ink2: "rgba(31,22,51,0.75)", ink3: "rgba(31,22,51,0.6)", ink4: "rgba(31,22,51,0.5)",
  line: "var(--color-hairline-cloud)", line2: "var(--color-hairline-cloud)",
  surface: "var(--color-surface-canvas-light)", surface2: "var(--color-surface-press-light)", surface3: "var(--color-surface-press-stronger)",
  green: "var(--color-success)", green50: "var(--color-success-bg)", green100: "var(--color-success-bg)",
  amber: "var(--color-warn)", amber50: "var(--color-warn-bg)", amber100: "var(--color-warn-bg)",
  live: "#c23d68", live50: "color-mix(in srgb, var(--color-accent-pink) 10%, transparent)", live100: "color-mix(in srgb, var(--color-accent-pink) 22%, transparent)",
  danger: "var(--color-danger)", danger50: "var(--color-danger-bg)", danger100: "var(--color-danger-bg)",
  blue: "var(--color-primary)", blue50: "rgba(37,99,235,.10)",
  purple: "var(--color-accent-violet-deep)", purple50: "color-mix(in srgb, var(--color-accent-violet-deep) 10%, transparent)",
  shadowSm: "var(--shadow-card-sm)",
  shadowMd: "var(--shadow-card-md)",
};

const CAT_COLOR = {
  firefighter: { bg: D.live50, color: D.live },
  ems: { bg: D.blue50, color: D.blue },
  hazmat: { bg: D.amber50, color: D.amber },
  rescue: { bg: D.green50, color: D.green },
  officer: { bg: D.purple50, color: D.purple },
};

function getIconStyle(tags = [], tPrimary = "var(--color-primary)") {
  const t = tags.map(s => s.toLowerCase()).join(" ");
  for (const [k, v] of Object.entries(CAT_COLOR)) if (t.includes(k)) return v;
  return { bg: alpha(tPrimary, 0.06), color: tPrimary };
}

function parseTags(raw = "[]") {
  try { const v = JSON.parse(raw); return Array.isArray(v) ? v : []; } catch { return raw.split(",").map(s => s.trim()).filter(Boolean); }
}

function parseQuestions(raw = "[]") {
  try { const v = JSON.parse(raw); return Array.isArray(v) ? v : []; } catch { return []; }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return "—"; }
}

function Ico({ size = 15, sw = 2, children, style }) {
  return <Box component="svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={style}>{children}</Box>;
}

function Btn({ children, primary, secondary, ghost, danger, sm, block, onClick, disabled, icon }) {
  const { palette } = useTheme();
  const tPrimary = palette.primary.main;
  const tPrimaryDk = palette.primary.dark;
  const h = sm ? 36 : 40;
  const base = { height: h, px: sm ? "13px" : "17px", borderRadius: sm ? "9px" : "11px", border: 0, fontFamily: "inherit", fontSize: sm ? "13px" : "13.5px", fontWeight: 700, cursor: disabled ? "default" : "pointer", display: "inline-flex", alignItems: "center", gap: "8px", transition: "all .13s", whiteSpace: "nowrap", width: block ? "100%" : undefined, justifyContent: block ? "center" : undefined };
  const v = primary ? { bgcolor: disabled ? D.surface3 : tPrimary, color: disabled ? D.ink3 : "var(--color-surface-canvas-light)", boxShadow: `0 4px 12px -4px ${alpha(tPrimary, 0.45)}`, "&:hover:not(:disabled)": { bgcolor: tPrimaryDk } }
    : danger ? { bgcolor: D.danger50, color: D.danger, border: `1.5px solid ${D.danger100}`, "&:hover": { bgcolor: D.danger100 } }
    : secondary ? { bgcolor: "var(--color-surface-canvas-light)", color: D.ink2, border: `1.5px solid ${D.line}`, "&:hover": { bgcolor: D.surface3, borderColor: "var(--color-hairline-cool)" } }
    : { bgcolor: "transparent", color: D.ink3, border: `1.5px solid ${D.line}`, "&:hover": { bgcolor: D.surface3 } };
  return <Box component="button" onClick={onClick} disabled={disabled} sx={{ ...base, ...v }}>{icon}{children}</Box>;
}

function IconBtn({ onClick, dangerHover, title, children }) {
  return (
    <Box component="button" title={title} onClick={onClick} sx={{ width: 36, height: 36, borderRadius: "9px", border: `1.5px solid ${D.line}`, bgcolor: "var(--color-surface-canvas-light)", display: "grid", placeItems: "center", cursor: "pointer", color: D.ink3, transition: "all .12s", flexShrink: 0, "&:hover": dangerHover ? { bgcolor: D.danger50, color: D.danger, borderColor: D.danger100 } : { bgcolor: D.surface3, color: D.ink, borderColor: "var(--color-hairline-cool)" } }}>
      {children}
    </Box>
  );
}

function Panel({ children, highlight, lime }) {
  const { palette } = useTheme();
  const tPrimary = palette.primary.main;
  return (
    <Box sx={{
      bgcolor: "var(--color-surface-canvas-light)",
      border: lime ? "1.5px solid var(--color-lime-18)" : highlight ? `1.5px solid ${alpha(tPrimary, 0.2)}` : `1px solid ${D.line}`,
      borderRadius: "16px",
      boxShadow: highlight ? `0 8px 26px -14px ${alpha(tPrimary, 0.34)}` : D.shadowSm,
      overflow: "hidden",
    }}>
      {children}
    </Box>
  );
}

function PanelHead({ icon, iconBg, iconColor, title, badge, action, tint }) {
  const { palette } = useTheme();
  const tPrimary = palette.primary.main;
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "16px 20px 14px", borderBottom: `1px solid ${tint ? "var(--color-lime-18)" : D.line2}`, bgcolor: tint ? "var(--color-lime-18)" : "transparent" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "11px" }}>
        <Box sx={{ width: 30, height: 30, borderRadius: "8px", display: "grid", placeItems: "center", bgcolor: iconBg || alpha(tPrimary, 0.06), color: iconColor || tPrimary, flexShrink: 0 }}>{icon}</Box>
        <Typography sx={{ fontSize: "15px", fontWeight: 600, letterSpacing: "-.01em", color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>{title}</Typography>
        {badge != null && <Box sx={{ fontSize: "11px", fontWeight: 700, px: "8px", py: "2px", borderRadius: "7px", bgcolor: D.surface3, color: D.ink3 }}>{badge}</Box>}
      </Box>
      {action}
    </Box>
  );
}

function Tag({ label }) {
  const t = label.toLowerCase();
  const cat = Object.entries(CAT_COLOR).find(([k]) => t.includes(k));
  if (cat) return <Box component="span" sx={{ fontSize: "11.5px", fontWeight: 700, px: "10px", py: "3px", borderRadius: "999px", bgcolor: cat[1].bg, color: cat[1].color, border: `1px solid color-mix(in srgb, ${cat[1].color} 30%, transparent)` }}>{label}</Box>;
  return <Box component="span" sx={{ fontSize: "11.5px", fontWeight: 700, px: "10px", py: "3px", borderRadius: "999px", bgcolor: D.surface3, color: D.ink2, border: `1px solid ${D.line}` }}>{label}</Box>;
}

function SetRow({ label, sub, value }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", py: "13px", borderBottom: `1px solid ${D.line2}`, "&:last-child": { borderBottom: 0, pb: 0 }, "&:first-of-type": { pt: 0 } }}>
      <Box>
        <Box sx={{ fontSize: "14px", fontWeight: 600, color: D.ink }}>{label}</Box>
        {sub && <Box sx={{ fontSize: "12.5px", color: D.ink3, mt: "2px" }}>{sub}</Box>}
      </Box>
      {value}
    </Box>
  );
}

function Pill({ children, green, live }) {
  return <Box component="span" sx={{ fontSize: "12.5px", fontWeight: 700, px: "11px", py: "4px", borderRadius: "999px", bgcolor: green ? D.green50 : live ? D.live50 : D.surface3, color: green ? D.green : live ? D.live : D.ink2, border: `1px solid ${green ? D.green100 : live ? D.live100 : D.line}` }}>{children}</Box>;
}

function MetaRow({ label, value }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", py: "11px", borderBottom: `1px solid ${D.line2}`, "&:last-child": { borderBottom: 0, pb: 0 }, "&:first-of-type": { pt: 0 } }}>
      <Box sx={{ fontSize: "12.5px", fontWeight: 600, color: D.ink3 }}>{label}</Box>
      <Box sx={{ fontSize: "13px", fontWeight: 700, color: D.ink, textAlign: "right" }}>{value}</Box>
    </Box>
  );
}

// ── Assign modal (reused pattern) ─────────────────────────────────────────────
function AssignModal({ open, onClose, itemId, alreadyAssigned, onAssigned, assignFn, label }) {
  const { palette } = useTheme();
  const tPrimary = palette.primary.main;
  const { courses, loading, error: loadError } = useAssignableClasses(open);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [assigning, setAssigning] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { if (open) { setSearch(""); setSelected(new Set()); } }, [open]);

  const assignedIds = new Set((alreadyAssigned || []).map(a => a.courseId));
  const filtered = courses.filter(c => !search || c.title?.toLowerCase().includes(search.toLowerCase()) || c.courseNumber?.toLowerCase().includes(search.toLowerCase()));
  const toggle = id => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const handleConfirm = async () => {
    if (!selected.size) return;
    setAssigning(true); setErr(null);
    try {
      await Promise.all([...selected].map(courseId => assignFn(courseId, { [label === "test" ? "testId" : "documentId"]: itemId })));
      onAssigned(); onClose();
    } catch (e) { setErr(e?.response?.data?.message || "Failed to assign."); }
    finally { setAssigning(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", gap: "13px", p: "22px 24px 18px", borderBottom: `1px solid ${D.line2}` }}>
        <Box sx={{ width: 40, height: 40, borderRadius: "11px", bgcolor: tPrimary, color: "var(--color-surface-canvas-light)", display: "grid", placeItems: "center", flexShrink: 0 }}>
          <Ico size={20}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></Ico>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ fontSize: "18px", fontWeight: 800, color: D.ink }}>Assign to Class</Box>
          <Box sx={{ fontSize: "13px", color: D.ink3, mt: "3px" }}>Select classes to add this {label} to.</Box>
        </Box>
        <Box component="button" onClick={onClose} sx={{ width: 32, height: 32, borderRadius: "8px", border: 0, bgcolor: D.surface3, color: D.ink3, cursor: "pointer", display: "grid", placeItems: "center", "&:hover": { bgcolor: D.danger50, color: D.danger } }}>
          <Ico size={16}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></Ico>
        </Box>
      </DialogTitle>
      <Box sx={{ p: "14px 24px", borderBottom: `1px solid ${D.line2}` }}>
        <Box sx={{ position: "relative" }}>
          <Box sx={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", color: D.ink4, display: "flex" }}><Ico size={15}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></Ico></Box>
          <Box component="input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search classes…" sx={{ height: 42, pl: "38px", pr: "14px", bgcolor: D.surface2, border: `1.5px solid ${D.line}`, borderRadius: "11px", fontFamily: "inherit", fontSize: "14px", color: D.ink, outline: "none", width: "100%", "&:focus": { borderColor: tPrimary, boxShadow: `0 0 0 3px ${alpha(tPrimary, 0.08)}`, bgcolor: "var(--color-surface-canvas-light)" } }} />
        </Box>
      </Box>
      <DialogContent sx={{ p: "10px 16px", maxHeight: 380, overflowY: "auto" }}>
        {(err || loadError) && <Alert severity="error" sx={{ mb: 1, borderRadius: "8px" }}>{err || loadError}</Alert>}
        {!loading && !loadError && filtered.length === 0 && <Box sx={{ textAlign: "center", color: D.ink3, fontSize: "13px", py: 4 }}>{search ? "No classes match your search." : "No classes available to assign to."}</Box>}
        {loading ? <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={24} sx={{ color: tPrimary }} /></Box>
          : filtered.map(c => <AssignClassRow key={c.id} course={c} assigned={assignedIds.has(c.id)} selected={selected.has(c.id)} onToggle={toggle} />)}
      </DialogContent>
      <DialogActions sx={{ p: "16px 24px", borderTop: `1px solid ${D.line2}`, bgcolor: D.surface2, display: "flex", justifyContent: "space-between" }}>
        <Box sx={{ fontSize: "12.5px", fontWeight: 600, color: D.ink3 }}>{selected.size} class{selected.size !== 1 ? "es" : ""} selected</Box>
        <Box sx={{ display: "flex", gap: "9px" }}>
          <Btn ghost sm onClick={onClose}>Cancel</Btn>
          <Btn primary sm onClick={handleConfirm} disabled={!selected.size || assigning} icon={assigning ? <CircularProgress size={12} sx={{ color: "inherit" }} /> : <Ico size={13} sw={2.5}><polyline points="20 6 9 17 4 12"/></Ico>}>Assign Selected</Btn>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// ── Question preview component ────────────────────────────────────────────────
function QuestionPreview({ question, index }) {
  const { palette } = useTheme();
  const tPrimary = palette.primary.main;
  const options = question.options || question.choices || [];
  const pairs = question.pairs || [];
  const questionText = question.questionText || question.text || question.question || question.criteria || "—";

  return (
    <Box sx={{ border: `1.5px solid ${D.line2}`, borderRadius: "13px", p: "15px 16px", bgcolor: "var(--color-surface-canvas-light)" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "11px", mb: "12px" }}>
        <Box sx={{ width: 26, height: 26, borderRadius: "8px", flexShrink: 0, bgcolor: tPrimary, color: "var(--color-surface-canvas-light)", display: "grid", placeItems: "center", fontSize: "12.5px", fontWeight: 800 }}>{index + 1}</Box>
        <Box sx={{ flex: 1, fontSize: "14.5px", fontWeight: 700, color: D.ink, lineHeight: 1.45, pt: "2px" }}>{questionText}</Box>
      </Box>
      {options.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "7px", pl: "37px" }}>
          {options.map((opt, oi) => {
            const isCorrect = typeof opt === "object" ? Boolean(opt.isCorrect) : false;
            return (
              <Box key={oi} sx={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "13.5px", color: isCorrect ? D.ink : D.ink2, fontWeight: isCorrect ? 700 : 400 }}>
                <Box sx={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${isCorrect ? D.green : D.line}`, bgcolor: isCorrect ? D.green : "var(--color-surface-canvas-light)", flexShrink: 0, display: "grid", placeItems: "center" }}>
                  {isCorrect && <Ico size={11} sw={3} style={{ color: "var(--color-surface-canvas-light)" }}><polyline points="20 6 9 17 4 12"/></Ico>}
                </Box>
                <Box component="span" sx={{ fontWeight: 700, color: isCorrect ? D.green : D.ink4, mr: "2px" }}>{String.fromCharCode(65 + oi)}.</Box>
                {typeof opt === "string" ? opt : opt.text || opt.label || ""}
              </Box>
            );
          })}
        </Box>
      )}
      {question.type === "SHORT_ANSWER" && (
        <Box sx={{ pl: "37px", fontSize: "13.5px", color: D.ink2 }}>
          <Box component="span" sx={{ fontWeight: 700, color: D.ink4, mr: "6px" }}>Accepted:</Box>
          {(question.acceptedAnswers || []).length > 0
            ? question.acceptedAnswers.map((a, ai) => (
              <Box key={ai} component="span" sx={{ fontWeight: 700, color: D.green, mr: "10px" }}>{a}</Box>
            ))
            : <Box component="span" sx={{ color: D.ink4 }}>none set</Box>}
        </Box>
      )}
      {pairs.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "7px", pl: "37px" }}>
          {pairs.map((pair, pi) => (
            <Box key={pair.id ?? pi} sx={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "13.5px" }}>
              <Box sx={{ flex: 1, color: D.ink2, fontWeight: 400 }}>{pair.prompt}</Box>
              <Ico size={13} sw={2.5} style={{ color: D.ink4, flexShrink: 0 }}><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></Ico>
              <Box sx={{ flex: 1, color: D.green, fontWeight: 700 }}>{pair.answer}</Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── Assigned classes sidebar panel ────────────────────────────────────────────
function AssignedPanel({ assignments, courseId: _unused, onUnassign, onAssign }) {
  return (
    <Panel lime>
      <PanelHead
        icon={<Ico size={15}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></Ico>}
        title="Assigned Classes"
        badge={assignments.length}
        tint
      />
      <Box sx={{ p: "10px 20px 14px" }}>
        {assignments.length === 0 ? (
          <Box sx={{ textAlign: "center", color: D.ink3, fontSize: "13px", py: "8px 0 4px" }}>Not assigned to any classes yet.</Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {assignments.map(a => (
              <Box key={a.assignmentId} sx={{ display: "flex", alignItems: "center", gap: "13px", py: "13px", borderBottom: `1px solid ${D.line2}`, "&:last-child": { borderBottom: 0, pb: "2px" }, "&:first-of-type": { pt: "2px" } }}>
                <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: D.live50, color: D.live, display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <Ico size={18}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></Ico>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ fontSize: "14px", fontWeight: 700, color: D.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.courseTitle}</Box>
                  {a.courseNumber && <Box sx={{ fontSize: "12.5px", color: D.ink3, mt: "1px", display: "flex", alignItems: "center", gap: "8px" }}><Box sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: "11.5px" }}>{a.courseNumber}</Box></Box>}
                </Box>
                {a.removable !== false && (
                  <IconBtn dangerHover title="Unassign" onClick={() => onUnassign(a)}>
                    <Ico size={13}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></Ico>
                  </IconBtn>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Panel>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────
export default function TestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { palette } = useTheme();
  const tPrimary = palette.primary.main;
  const tAccent = palette.warning.main;

  const [test, setTest] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, a, v] = await Promise.all([
        fetchTestBankItem(id),
        fetchTestBankAssignments(id).catch(() => []),
        fetchTestBankVersions(id).catch(() => []),
      ]);
      setTest(t);
      setAssignments(Array.isArray(a) ? a : []);
      setVersions(Array.isArray(v) ? v : []);
    } catch { setErr("Failed to load test."); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleEditSave = async ({ title, description, passingScore, timeLimitMinutes, maxRetakes, questions, changeNote, autosave }) => {
    const payload = {
      title, description,
      passingScore: passingScore ?? null,
      timeLimitMin: timeLimitMinutes ?? null,
      maxRetakes: maxRetakes ?? 0,
      questions: JSON.stringify(Array.isArray(questions) ? questions : []),
    };
    if (changeNote !== undefined) payload.changeNote = changeNote;
    if (autosave) {
      // Auto-save updates the page in place; a reload would replace the editor's content while it is being edited.
      const updated = await updateTestBankItem(id, payload);
      setTest((prev) => ({ ...prev, ...updated }));
      return { id: Number(id) };
    }
    await updateTestBankItem(id, payload);
    await load();
  };

  const handleDuplicate = async () => {
    try {
      const copy = await createTestBankItem({ title: `${test.title} (copy)`, description: test.description, passingScore: test.passingScore, timeLimitMin: test.timeLimitMin, maxRetakes: test.maxRetakes, questions: test.questions, tags: test.tags });
      navigate(`/instructor/knowledge-bank/tests/${copy.id}`);
    } catch { setErr("Failed to duplicate."); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await deleteTestBankItem(id); navigate("/instructor/knowledge-bank"); }
    catch (e) { setErr(e?.response?.data?.message || "Cannot delete."); setDeleteConfirm(false); }
    finally { setDeleting(false); }
  };

  const handleUnassign = async (a) => {
    try { await unassignTestFromCourse(id, a.courseId); setAssignments(prev => prev.filter(x => x.assignmentId !== a.assignmentId)); }
    catch { setErr("Failed to unassign."); }
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", pt: 10 }}><CircularProgress sx={{ color: tPrimary }} /></Box>;
  if (err && !test) return <Box sx={{ maxWidth: 600, mx: "auto", pt: 6 }}><Alert severity="error">{err}</Alert></Box>;
  if (!test) return null;

  const tags = parseTags(test.tags);
  const questions = parseQuestions(test.questions);
  const { bg: iconBg, color: iconColor } = getIconStyle(tags, tPrimary);

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2, borderRadius: "10px" }}>{err}</Alert>}

      <Box component="button" onClick={() => navigate("/instructor/knowledge-bank")}
        sx={{ display: "inline-flex", alignItems: "center", gap: "7px", fontSize: "13px", fontWeight: 600, color: D.ink3, border: 0, bgcolor: "transparent", cursor: "pointer", mb: "16px", p: 0, "&:hover": { color: tPrimary } }}>
        <Ico size={15} sw={2.2}><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></Ico>
        Back to Knowledge Bank
      </Box>

      {/* Hero */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "18px", mb: "20px", flexWrap: "wrap" }}>
        <Box sx={{ width: 56, height: 56, borderRadius: "15px", bgcolor: iconBg, color: iconColor, display: "grid", placeItems: "center", flexShrink: 0 }}>
          <Ico size={26}><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></Ico>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "28px", fontWeight: 600, letterSpacing: "-.01em", lineHeight: 1.15, color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>{test.title}</Typography>
          {test.description && <Box sx={{ fontSize: "14.5px", color: D.ink3, mt: "6px", maxWidth: 560 }}>{test.description}</Box>}
          {tags.length > 0 && <Box sx={{ display: "flex", alignItems: "center", gap: "7px", flexWrap: "wrap", mt: "11px" }}>{tags.map(t => <Tag key={t} label={t} />)}</Box>}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0, flexWrap: "wrap" }}>
          <IconBtn title="Duplicate" onClick={handleDuplicate}><Ico size={15}><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></Ico></IconBtn>
          <Btn secondary sm onClick={() => setEditOpen(true)} icon={<Ico size={14}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></Ico>}>Edit Test</Btn>
          <Btn primary sm onClick={() => setAssignOpen(true)} icon={<Ico size={14}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></Ico>}>Assign to Class</Btn>
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 340px" }, gap: "20px", alignItems: "start" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 }}>

          {/* Questions panel */}
          <Panel>
            <PanelHead icon={<Ico size={15}><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></Ico>} title="Question Preview" badge={`${questions.length} question${questions.length !== 1 ? "s" : ""}`}
              action={<Btn secondary sm onClick={() => setEditOpen(true)} icon={<Ico size={13}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></Ico>}>Edit Questions</Btn>} />
            {questions.length > 0 && (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: "11px", mx: "20px", mt: "14px", p: "13px 15px", bgcolor: "var(--color-surface-canvas-light)", border: `1px solid ${D.line}`, borderRadius: "12px", fontSize: "13px", color: D.ink2, lineHeight: 1.55 }}>
                <Ico size={16} style={{ color: tPrimary, flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></Ico>
                {showAllQuestions || questions.length <= 3
                  ? `Showing all ${questions.length} question${questions.length !== 1 ? "s" : ""}.`
                  : `Showing the first 3 of ${questions.length} questions.`} Correct answers highlighted in green.
              </Box>
            )}
            <Box sx={{ p: "14px 20px", display: "flex", flexDirection: "column", gap: "14px" }}>
              {questions.length === 0 ? (
                <Box sx={{ textAlign: "center", py: "24px", color: D.ink4, fontSize: "13.5px" }}>No questions yet. Click "Edit Questions" to add content.</Box>
              ) : (
                <>
                  {(showAllQuestions ? questions : questions.slice(0, 3)).map((q, i) => <QuestionPreview key={i} question={q} index={i} />)}
                  {questions.length > 3 && (
                    <Box sx={{ textAlign: "center", pt: "2px" }}>
                      <Btn ghost sm onClick={() => setShowAllQuestions((v) => !v)}>
                        {showAllQuestions ? "Show fewer questions" : `View all ${questions.length} questions`}
                      </Btn>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Panel>

          {/* Scoring panel */}
          <Panel>
            <PanelHead icon={<Ico size={15}><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></Ico>} iconBg={alpha(tAccent, 0.1)} iconColor={tAccent} title="Scoring & Evaluation" />
            <Box sx={{ p: "10px 20px 18px" }}>
              <SetRow label="Passing Score" sub="Minimum percentage required to pass" value={<Pill green>{test.passingScore != null ? `${test.passingScore}%` : "Not set"}</Pill>} />
              <SetRow label="Time Limit" sub="Maximum time to complete the exam" value={<Pill live>{test.timeLimitMin ? `${test.timeLimitMin} minutes` : "No limit"}</Pill>} />
              <SetRow label="Question Count" sub="Total questions in this test" value={<Pill>{questions.length} questions</Pill>} />
              <SetRow label="Retake Attempts" sub="Attempts before remediation required" value={<Pill>{test.maxRetakes ?? 0} attempt{(test.maxRetakes ?? 0) !== 1 ? "s" : ""}</Pill>} />
            </Box>
          </Panel>
        </Box>

        {/* Sidebar */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", position: { lg: "sticky" }, top: { lg: "76px" } }}>
          <AssignedPanel assignments={assignments} onUnassign={handleUnassign} onAssign={() => setAssignOpen(true)} />

          <Panel>
            <PanelHead icon={<Ico size={15}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></Ico>} iconBg={D.surface3} iconColor={D.ink3} title="Details" />
            <Box sx={{ p: "10px 20px 18px" }}>
              <MetaRow label="Created" value={fmtDate(test.createdAt)} />
              <MetaRow label="Last edited" value={fmtDate(test.updatedAt)} />
              <MetaRow label="Questions" value={`${questions.length} total`} />
              <MetaRow label="Time limit" value={test.timeLimitMin ? `${test.timeLimitMin} min` : "None"} />
              <MetaRow label="Passing score" value={test.passingScore != null ? `${test.passingScore}%` : "Not set"} />
            </Box>
          </Panel>

          {/* Version History */}
          <Panel>
            <PanelHead icon={<Ico size={15}><path d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16z"/><path d="M12 8v4l3 3"/></Ico>} iconBg={D.surface3} iconColor={D.ink3} title="Version History" badge={versions.length || null} />
            <Box sx={{ p: "10px 20px 14px" }}>
              {versions.length === 0 ? (
                <Box sx={{ textAlign: "center", color: D.ink3, fontSize: "13px", py: "8px 0 4px" }}>No version history yet.</Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {versions.map((v, idx) => (
                    <Box key={v.id} sx={{ display: "flex", alignItems: "flex-start", gap: "12px", py: "13px", borderBottom: `1px solid ${D.line2}`, "&:last-child": { borderBottom: 0, pb: "2px" }, "&:first-of-type": { pt: "2px" } }}>
                      <Box sx={{
                        width: 24, height: 24, borderRadius: "50%", flexShrink: 0, mt: "1px",
                        display: "grid", placeItems: "center", fontSize: "11px", fontWeight: 800,
                        bgcolor: idx === 0 ? tPrimary : D.surface3,
                        color: idx === 0 ? "var(--color-surface-canvas-light)" : D.ink3,
                      }}>
                        {v.versionNumber}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ fontSize: "13.5px", fontWeight: 700, color: D.ink }}>
                          v{v.versionNumber} — {v.changeNote || "Updated test"}
                        </Box>
                        <Box sx={{ fontSize: "12px", color: D.ink3, mt: "2px" }}>
                          {(v.createdByName || "Unknown")} · {fmtDate(v.createdAt)}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Panel>

          <Panel>
            <Box sx={{ p: "18px 20px", display: "flex", flexDirection: "column", gap: "9px" }}>
              <Btn secondary block icon={<Ico size={14}><path d="M21 8v13H3V8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></Ico>}>Archive Test</Btn>
              <Btn danger block onClick={() => setDeleteConfirm(true)} icon={<Ico size={14}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></Ico>}>Delete Test</Btn>
            </Box>
          </Panel>
        </Box>
      </Box>

      <CreateTestModal bank open={editOpen} onClose={() => setEditOpen(false)} onSave={handleEditSave} initial={test ? { id: test.id, enabled: test.enabled, title: test.title, description: test.description, passingScore: test.passingScore, timeLimitMinutes: test.timeLimitMin, maxRetakes: test.maxRetakes, questions: parseQuestions(test.questions) } : null} />

      <AssignModal open={assignOpen} onClose={() => setAssignOpen(false)} itemId={id} alreadyAssigned={assignments} onAssigned={() => fetchTestBankAssignments(id).then(d => setAssignments(Array.isArray(d) ? d : []))} assignFn={(courseId) => assignBankTestAsClassLevel(courseId, test)} label="test" />

      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)} PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontSize: "17px", fontWeight: 800, color: D.ink }}>Delete Test?</DialogTitle>
        <DialogContent><Typography sx={{ fontSize: "13.5px", color: D.ink3 }}>This will permanently delete "{test.title}". Cannot be undone.</Typography></DialogContent>
        <DialogActions sx={{ p: "12px 24px 18px", gap: "8px" }}>
          <Btn ghost sm onClick={() => setDeleteConfirm(false)}>Cancel</Btn>
          <Btn danger sm onClick={handleDelete} disabled={deleting} icon={deleting ? <CircularProgress size={12} sx={{ color: "inherit" }} /> : null}>{deleting ? "Deleting…" : "Delete"}</Btn>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
